import { useEffect, useEffectEvent } from "react";

import { useCompanionV2 } from "./use-companion-v2";

export const TIME_IN_EDITOR_THRESHOLD_MS = 5 * 60 * 1000;
export const TIME_IN_EDITOR_IDLE_MS = 30 * 1000;

const TOOLTIP_CONTENT =
  "I noticed you're spending time on this note. Want me to help clean it up or turn these edits into a template?";

const ACTIONS: {
  label: string;
  message: string;
  key: "clean_up" | "create_template";
}[] = [
  {
    label: "Clean up",
    message: "Help me clean up this note",
    key: "clean_up",
  },
  {
    label: "Create template",
    message: "Help me turn these edits into a template",
    key: "create_template",
  },
];

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "keydown",
  "pointermove",
  "pointerdown",
];

interface UseTimeInEditorTriggerInput {
  noteUuid: string | null | undefined;
  enabled: boolean;
}

export function useTimeInEditorTrigger({
  noteUuid,
  enabled,
}: UseTimeInEditorTriggerInput): void {
  const { showCompanionTooltip } = useCompanionV2();

  const fireTooltip = useEffectEvent((uuid: string) => {
    showCompanionTooltip({
      id: `time-in-editor-${uuid}`,
      companions: ["quill"],
      content: TOOLTIP_CONTENT,
      actions: ACTIONS,
      tracking: {
        trigger: "time_in_editor",
        properties: { noteUuid: uuid },
      },
    });
  });

  useEffect(() => {
    if (!enabled || !noteUuid) return;

    let activeMs = 0;
    let lastActivityAt = Date.now();

    const bumpActivity = () => {
      lastActivityAt = Date.now();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, bumpActivity, {
        passive: true,
      });
    });

    const intervalId = setInterval(() => {
      if (Date.now() - lastActivityAt < TIME_IN_EDITOR_IDLE_MS) {
        activeMs += TIME_IN_EDITOR_IDLE_MS;
      }
      if (activeMs < TIME_IN_EDITOR_THRESHOLD_MS) return;

      clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((event) =>
        document.removeEventListener(event, bumpActivity),
      );
      fireTooltip(noteUuid);
    }, TIME_IN_EDITOR_IDLE_MS);

    return () => {
      clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((event) =>
        document.removeEventListener(event, bumpActivity),
      );
    };
  }, [enabled, noteUuid]);
}
